import {
    Button,
    Controls,
    ControlsItem,
    KeyboardArrowBottomIcon,
    Link,
    Menu,
    MenuHandler,
    MenuItem,
    Switch,
    Typography
} from '@genestack/ui';
import React from 'react';

import {
    GraphViewKind,
    QueryGraphLayoutAlgorithm,
    ResultMaxLengthOption
} from '../../../../../components/graph/interface';
import styles from './query-results-header.module.css';

import {MaxPathLengthMenuItem, unlimitedLengthOptionLabel} from './max-path-length-menu-item';
import {GraphLegend} from '../../../../../components/graph/components/graph-legend/graph-legend';
import {MaxPathLengthOptionResult} from '../results-graph/requests/use-query-result-max-length-options';
import {PresentationMenu} from './presentation-menu';
import {GraphPresentationPropsLoaded} from '../results-graph/use-graph-presentation-props';
import {useGraphSettings} from '../results-graph/use-graph-settings';
import {usePresentationEditedProps} from '../results-graph/use-presentation-edited-props';
import {useGraphPresentationActions} from '../results-graph/use-graph-presentation-actions';
import {useIsCantDisplayGraph} from '../results-graph/use-is-cant-display-graph';
import {useQueryGraphActions} from '../results-graph/use-query-graph-actions';

interface Props {
    queryId: number;
    maxLengthOptions: MaxPathLengthOptionResult;
    layoutAlgorithm: QueryGraphLayoutAlgorithm;
    selectLayoutAlgorithm: (layout: QueryGraphLayoutAlgorithm, isRedoAction?: false) => void;
    isCustomLayout: boolean;
    graphSettingsProps: ReturnType<typeof useGraphSettings>;
    graphPresentationProps: GraphPresentationPropsLoaded;
    presentationEditedProps: ReturnType<typeof usePresentationEditedProps>;
    presentationActionsProps: ReturnType<typeof useGraphPresentationActions>;
    graphActionsProps: ReturnType<typeof useQueryGraphActions>;
    clearUnsavedPresentationDetails: () => void;
    isCalculatingLayout: boolean;
    isCantDisplayGraphProps: ReturnType<typeof useIsCantDisplayGraph>;
}

export const QueryResultsHeader = (props: Props) => {
    const {viewKind, maxPathLength} = props.graphSettingsProps;
    const {maxPathLengthSelectedAction, viewKindToggledAction} = props.graphActionsProps;
    const onMaxPathLengthSelected = React.useCallback(
        (mpLength: number) => maxPathLengthSelectedAction(mpLength),
        [maxPathLengthSelectedAction]
    );
    const limitsMenu = (limitInfos: ResultMaxLengthOption[]) => (
        <Menu onValueSelect={onMaxPathLengthSelected}>
            {limitInfos.map((option, index) => {
                return (
                    <MaxPathLengthMenuItem
                        option={option}
                        isLastOption={index === limitInfos.length - 1}
                        isSelected={option.pathLength === maxPathLength}
                        key={option.pathLength}
                    />
                );
            })}
        </Menu>
    );

    // this just prevents the event to be passed as a second parameter
    const selectAlgorithm = React.useCallback(
        (algo: QueryGraphLayoutAlgorithm) => props.selectLayoutAlgorithm(algo),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.selectLayoutAlgorithm]
    );
    const layoutMenu = () => (
        <Menu onValueSelect={selectAlgorithm}>
            {props.isCustomLayout && (
                <MenuItem key="custom" value="custom" active>
                    {props.layoutAlgorithm} (custom)
                </MenuItem>
            )}

            {Object.values(QueryGraphLayoutAlgorithm).map((layoutOption) => (
                <MenuItem key={layoutOption} value={layoutOption}>
                    {layoutOption}
                </MenuItem>
            ))}
        </Menu>
    );

    const maxPathLengthOptions = props.maxLengthOptions[viewKind];
    const isFullGraphView =
        maxPathLengthOptions?.length &&
        maxPathLengthOptions[maxPathLengthOptions.length - 1].pathLength === maxPathLength;

    const {isResultIncomplete, isCantDisplayGraph} = props.isCantDisplayGraphProps;

    const maxPathLengthBtnLabel = (function () {
        if (isFullGraphView) {
            return unlimitedLengthOptionLabel;
        }

        return [
            'Max. path length',
            isCantDisplayGraph ? undefined : `- ${maxPathLength} edges`
        ].join('');
    })();

    return (
        <div className={styles.root}>
            <Controls flexWrap="wrap">
                <ControlsItem grow>
                    {!isCantDisplayGraph && (
                        <PresentationMenu
                            presentationProps={props.graphPresentationProps}
                            presentationEditedProps={props.presentationEditedProps}
                            clearUnsavedPresentationDetails={props.clearUnsavedPresentationDetails}
                            queryId={props.queryId}
                            presentationActionsProps={props.presentationActionsProps}
                            isCalculatingLayout={props.isCalculatingLayout}
                        />
                    )}
                </ControlsItem>

                <ControlsItem>
                    <Typography as="label">
                        <Controls>
                            <ControlsItem>
                                <Switch
                                    checked={viewKind === GraphViewKind.MERGED}
                                    onCheckedChange={viewKindToggledAction}
                                    disabled={isResultIncomplete}
                                />
                            </ControlsItem>
                            <ControlsItem as={Link}>Collapse matched</ControlsItem>
                        </Controls>
                    </Typography>
                </ControlsItem>

                <ControlsItem>
                    <MenuHandler menu={layoutMenu()}>
                        <Button
                            size="small"
                            iconEnd={<KeyboardArrowBottomIcon />}
                            disabled={isCantDisplayGraph}
                        >
                            {props.layoutAlgorithm}
                            {props.isCustomLayout && '(custom)'}
                        </Button>
                    </MenuHandler>
                </ControlsItem>

                {maxPathLengthOptions && (
                    <ControlsItem>
                        <MenuHandler menu={limitsMenu(maxPathLengthOptions)}>
                            <Button
                                size="small"
                                iconEnd={<KeyboardArrowBottomIcon />}
                                disabled={isResultIncomplete}
                            >
                                {maxPathLengthBtnLabel}
                            </Button>
                        </MenuHandler>
                    </ControlsItem>
                )}

                <GraphLegend disabled={isCantDisplayGraph} />
            </Controls>
        </div>
    );
};
